import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { SafeNotes, StatusChangeNote, groupInteractionsWithTrigger } from './HistoryLogRenderers';
import { HistoryTableProps } from './HistoryNotesPanel.props';
import { InteractionDetails, InteractionLogEntry } from './InteractionLogEntry';
import { useLogFileViewer, isFileAttachmentLog } from './useLogFileViewer';
import { displayLogTypeName } from '../../../../../@types/service.type';
import { COLORS } from '../../../../../configs/colors';
import { formatAttendanceNote } from '../../../../../utils/attendanceNote';

/** Log type codes that represent reported/creation notes — shown in the Reported Information panel instead. */
const CREATION_NOTE_CODES = new Set(['creation-notes', 'creation_notes', 'reported-notes']);

const cellSx = { fontSize: '12px', py: 0.75, px: 1.5 };
const headerSx = {
  ...cellSx,
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '11px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

const NOTE_C = { bg: COLORS.HistoryNoteBg, text: COLORS.HistoryNoteText };
const ATT_C = { bg: COLORS.HistoryAttendanceBg, text: COLORS.HistoryAttendanceText };
const STS_C = { bg: COLORS.HistoryStatusBg, text: COLORS.HistoryStatusText };
const SYS_C = { bg: COLORS.HistorySystemBg, text: COLORS.HistorySystemText };
const typeColors: Record<string, { bg: string; text: string }> = {
  notes: NOTE_C,
  note: NOTE_C,
  email: ATT_C,
  'status-change': STS_C,
  status: STS_C,
  system: SYS_C,
  attendance: ATT_C,
  'check-in': ATT_C,
  'check-out': ATT_C,
  allocation: { bg: COLORS.HistoryAssignmentBg, text: COLORS.HistoryAssignmentText },
  'part-action': { bg: COLORS.HistoryPartsBg, text: COLORS.HistoryPartsText },
};

const getTypeChipColors = (typeCode: string): { bg: string; text: string } => {
  const lower = String(typeCode).toLowerCase();
  return typeColors[lower] ?? { bg: COLORS.HistorySystemBg, text: COLORS.HistorySystemText };
};

const formatDateTime = (dateStr: unknown): string => {
  if (!dateStr || typeof dateStr !== 'string') return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return `${d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  } catch {
    return '-';
  }
};

/** Map filter keys to their matching logTypeCodes. */
const FILTER_CODE_MAP: Record<string, string[]> = {
  notes: ['notes'],
  status: ['status-change', 'status'],
  parts: [
    'part-actions-install-serialised',
    'part-actions-remove-serialised',
    'part-actions-replace-serialised',
    'part-actions-doa-serialised',
    'part-actions-clean-serialised',
    'part-actions-test-serialised',
    'part-actions-repair-serialised',
    'part-actions-adjust-serialised',
  ],
  assignment: ['assignment-events-primary', 'assignment-events-secondary', 'allocation'],
  attendance: ['check-in', 'check-out', 'attendance'],
  uploads: ['uploads'],
  interactions: ['interaction-events'],
};

const PART_ACTION_CODES = new Set(FILTER_CODE_MAP.parts);

/** Extract the verb from a part action code like "part-actions-install-serialised" → "Install" */
const extractPartActionVerb = (code: string): string => {
  const match = code.match(/^part-actions-(\w+)/);
  if (!match) return code;
  const verb = match[1].replace(/-.*$/, '');
  return verb.charAt(0).toUpperCase() + verb.slice(1);
};

const isPartActionLog = (code: string): boolean =>
  PART_ACTION_CODES.has(code) || code.startsWith('part-actions-');

const isStatusChange = (code: string): boolean => {
  const normalized = String(code).toLowerCase();
  return normalized === 'status-change' || normalized === 'status';
};

/**
 * Resolve the Action-column badge. Cancel / Futile / Re-open are status changes too, but are
 * special and deserve a distinct badge — red "Job Cancelled", red "Job Futiled", amber
 * "Job Re-Opened" — so they stand out from ordinary blue status changes. Detection is by the
 * to/from status name (logs carry serviceStatusName, not a code). Everything else is unchanged.
 */
const resolveActionBadge = (
  typeCode: string,
  defaultLabel: string,
  defaultColors: { bg: string; text: string },
  fromStatus?: string,
  toStatus?: unknown
): { label: string; bg: string; text: string } => {
  if (isStatusChange(typeCode)) {
    const to = String(toStatus ?? '').toLowerCase();
    const from = String(fromStatus ?? '').toLowerCase();
    if (to.includes('cancel')) {
      return {
        label: 'Job Cancelled',
        bg: COLORS.HistoryCancelledBg,
        text: COLORS.HistoryCancelledText,
      };
    }
    if (to.includes('futile')) {
      return {
        label: 'Job Futiled',
        bg: COLORS.HistoryCancelledBg,
        text: COLORS.HistoryCancelledText,
      };
    }
    if (from.includes('cancel') || from.includes('futile')) {
      return {
        label: 'Job Re-Opened',
        bg: COLORS.HistoryReopenedBg,
        text: COLORS.HistoryReopenedText,
      };
    }
  }
  return { label: defaultLabel, bg: defaultColors.bg, text: defaultColors.text };
};

export const HistoryTable: React.FC<HistoryTableProps> = ({
  logs,
  serviceId,
  filterType,
  sortDirection,
  onEditNote,
  onDeleteNote,
  canEditNotes = false,
  canDeleteNotes = false,
  highlightPartActions = false,
}) => {
  const showActions = canEditNotes || canDeleteNotes;
  // Click-to-view for file references embedded in notes (uploads / interactions /
  // serialised part actions) — restores the legacy behaviour (UI-96).
  const { openFromLog, viewer } = useLogFileViewer(serviceId);
  // Exclude creation/reported notes — those are shown in the Reported Information panel
  const nonCreationLogs = logs.filter(
    (log) => !CREATION_NOTE_CODES.has(String(log.logTypeCode ?? '').toLowerCase())
  );

  const typeFiltered =
    filterType === 'all'
      ? nonCreationLogs
      : nonCreationLogs.filter((log) => {
          const code = String(log.logTypeCode ?? log.type ?? '').toLowerCase();
          const codes = FILTER_CODE_MAP[filterType];
          if (codes) return codes.some((c) => code.includes(c));
          return code === filterType.toLowerCase();
        });

  const sorted = [...typeFiltered].sort((a, b) => {
    const timeA = new Date(String(a.time ?? a.createdAt ?? '')).getTime() || 0;
    const timeB = new Date(String(b.time ?? b.createdAt ?? '')).getTime() || 0;
    return sortDirection === 'newest' ? timeB - timeA : timeA - timeB;
  });

  const filteredLogs = groupInteractionsWithTrigger(sorted);

  // Build from-status map: for each status-change log, find the previous status
  // by walking all logs in chronological order
  const fromStatusMap = new Map<string | number, string>();
  const chronological = [...nonCreationLogs].sort((a, b) => {
    const timeA = new Date(String(a.time ?? a.createdAt ?? '')).getTime() || 0;
    const timeB = new Date(String(b.time ?? b.createdAt ?? '')).getTime() || 0;
    return timeA - timeB;
  });
  let prevStatus = '';
  for (const log of chronological) {
    const code = String(log.logTypeCode ?? '').toLowerCase();
    const currentStatus = String(log.serviceStatusName ?? '');
    if (code === 'status-change' || code === 'status') {
      fromStatusMap.set(log.id as string | number, prevStatus);
    }
    if (currentStatus) prevStatus = currentStatus;
  }

  if (filteredLogs.length === 0) {
    return (
      <Typography
        sx={{
          fontSize: '13px',
          color: 'text.secondary',
          py: 2,
          textAlign: 'center',
        }}
      >
        No history entries
      </Typography>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerSx, width: 120 }}>Time</TableCell>
              <TableCell sx={{ ...headerSx, width: 100 }}>User</TableCell>
              <TableCell sx={{ ...headerSx, width: 80 }}>Status</TableCell>
              <TableCell sx={{ ...headerSx, width: 90 }}>Action</TableCell>
              <TableCell sx={headerSx}>Notes</TableCell>
              {showActions && <TableCell sx={{ ...headerSx, width: 70 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log, idx) => {
              const typeCode = String(log.logTypeCode ?? log.type ?? '-');
              const chipColors = getTypeChipColors(typeCode);
              const notes = formatAttendanceNote(
                String(log.notes ?? '-'),
                log.logTypeCode ?? log.type,
                log.time ?? log.createdAt ?? log.dateTime
              );

              const isPartAction = isPartActionLog(typeCode.toLowerCase());
              // For repair views, show just the verb (Install, Remove, etc.) instead of the full name
              const typeName =
                highlightPartActions && isPartAction
                  ? extractPartActionVerb(typeCode)
                  : displayLogTypeName(typeCode, String(log.logTypeName ?? typeCode));
              const partHighlight = highlightPartActions && isPartAction;
              const fromStatus = fromStatusMap.get(log.id as string | number);
              const actionBadge = resolveActionBadge(
                typeCode,
                typeName,
                chipColors,
                fromStatus,
                log.serviceStatusName
              );

              return (
                <TableRow
                  key={String(log.id ?? idx)}
                  sx={{
                    bgcolor: partHighlight
                      ? 'rgba(255, 193, 7, 0.12)'
                      : idx % 2 === 0
                        ? 'transparent'
                        : 'v4.tableEven',
                    '&:hover': {
                      bgcolor: partHighlight ? 'rgba(255, 193, 7, 0.2)' : 'v4.tableHover',
                    },
                  }}
                >
                  <TableCell sx={{ ...cellSx, width: 120, whiteSpace: 'nowrap' }}>
                    {formatDateTime(log.time ?? log.createdAt ?? log.dateTime)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 100 }}>
                    {String(log.userName ?? log.user ?? log.createdBy ?? '-')}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 80 }}>
                    {String(log.serviceStatusName ?? log.status ?? '-')}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 90 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.25,
                        borderRadius: 2,
                        fontSize: partHighlight ? '11px' : '10px',
                        fontWeight: partHighlight ? 700 : 600,
                        bgcolor: actionBadge.bg,
                        color: actionBadge.text,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {actionBadge.label}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {isStatusChange(typeCode) ? (
                      <StatusChangeNote
                        fromStatus={fromStatus}
                        toStatus={log.serviceStatusName}
                        notes={notes}
                      />
                    ) : isFileAttachmentLog(typeCode) ? (
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={(e) =>
                          openFromLog(
                            log,
                            e.target instanceof HTMLElement ? e.target.outerHTML : ''
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openFromLog(log, e.currentTarget.outerHTML);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SafeNotes html={notes} />
                      </Box>
                    ) : (
                      <SafeNotes html={notes} />
                    )}
                    <InteractionDetails data={log.data} serviceId={serviceId} />
                    {(log._attachedInteractions as Array<Record<string, unknown>> | undefined)?.map(
                      (iLog, iIdx) => (
                        <InteractionLogEntry
                          key={(iLog.id as number | string | undefined) ?? iIdx}
                          notes={String(iLog.notes ?? '')}
                          data={iLog.data}
                          serviceId={serviceId}
                        />
                      )
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.25,
                          opacity: 0,
                          transition: 'opacity 0.15s',
                          'tr:hover &': { opacity: 1 },
                          'tr:focus-within &': { opacity: 1 },
                        }}
                      >
                        {canEditNotes && (
                          <Tooltip title="Edit note">
                            <IconButton
                              size="small"
                              aria-label="edit note"
                              onClick={() => onEditNote?.(log.id as number)}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteNotes && (
                          <Tooltip title="Hide record">
                            <IconButton
                              size="small"
                              aria-label="hide record"
                              onClick={() => onDeleteNote?.(log.id as number)}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {viewer}
    </>
  );
};
